import { PlusCircleOutlined } from '@ant-design/icons'
import { useClickAway } from 'ahooks'
import { Tag, Tooltip, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { WorkResourceCustomSearch } from '@/components/work-resource/click-away/custom-search.component'
import { WorkResourceRowType } from '@/schemas'

import { ProjectWorkResourceInput } from '../special-project/special-project-work-resource.container'

export const ClickAwaySubProjectCustomSearch: React.FC<{
  readonly?: boolean
  record: ProjectWorkResourceInput
  projectWorkResourceSelfId?: string
  onCreate: (() => void) | undefined
  onChange: (value: {
    displayName: string
    subprojectWorkResourceName: string | null
    subprojectWorkResourceSelfId?: string
  }) => void
}> = ({ record, readonly, projectWorkResourceSelfId, onCreate, onChange }) => {
  const { t } = useTranslation()

  const isProject = record.type === WorkResourceRowType.Project
  const isCustom = record.type === WorkResourceRowType.Custom
  const isPortfolio = record.type === WorkResourceRowType.Portfolio
  const isSubprojectRow = isProject || isCustom

  const ref = React.useRef<HTMLDivElement>(null)

  const [isActive, setIsActive] = React.useState(record.isCreating)
  const [value, setValue] = React.useState<{
    displayName: string
    subprojectWorkResourceName: string | null
    subprojectWorkResourceSelfId?: string
  }>({
    displayName: record.subprojectName || record.project?.name || '',
    subprojectWorkResourceName: record.subprojectName,
    subprojectWorkResourceSelfId: record.subprojectWorkResourceSelfId || undefined,
  })

  const defaultOptions =
    value.subprojectWorkResourceSelfId && value.subprojectWorkResourceName
      ? [
          {
            value: value.subprojectWorkResourceSelfId,
            label: value.subprojectWorkResourceName,
            id: value.subprojectWorkResourceSelfId,
          },
        ]
      : []

  useClickAway(() => {
    if (!readonly && isActive && !record.isCreating) {
      onChange(value)
      setIsActive(false)
    }
  }, [ref])

  React.useEffect(() => {
    if (record.isCreating && record.subprojectName !== value.subprojectWorkResourceName) {
      onChange(value)
    }
  }, [value])

  const portfolio = record.portfolio
  const project = record.project

  const departmentIcon =
    isSubprojectRow && !readonly ? (
      <div className='flex gap-2 items-center'>
        <PlusCircleOutlined
          onClick={(e) => {
            onCreate && onCreate()
            e.stopPropagation()
          }}
        />
      </div>
    ) : null

  const portfolioComponent = (
    <Tooltip title={portfolio?.name}>
      <Typography.Link
        className='overflow-hidden !max-w-24 break-all'
        href={`${window.location.origin}/portfolio/${portfolio?.selfId}`}
        // ellipsis
        rel='noopener noreferrer'
        target='_blank'
      >
        {portfolio?.name}
      </Typography.Link>
    </Tooltip>
  )

  const projectComponent = (
    <Tooltip title={project?.name}>
      <Typography.Link
        className='overflow-hidden !max-w-24 break-all'
        href={`${window.location.origin}/project/${project?.selfId}`}
        // ellipsis
        rel='noopener noreferrer'
        target='_blank'
      >
        {project?.name}
      </Typography.Link>
    </Tooltip>
  )

  const customComponent = (
    <Tooltip title={record.subprojectName}>
      <Typography.Text
        // ellipsis
        onClick={(e) => {
          if (!readonly) {
            setIsActive(true)
            e?.stopPropagation()
          }
        }}
        className='overflow-hidden !max-w-24 break-all'
      >
        {record.subprojectName}
      </Typography.Text>
    </Tooltip>
  )

  const tag = (
    <span className='ml-2'>
      {isPortfolio ? (
        <Tag color='blue'>{t('PORTFOLIO')}</Tag>
      ) : isProject ? (
        <Tag color='green'>{t('PROJECT')}</Tag>
      ) : isCustom ? (
        <Tag color='gold'>{t('work.resource.subproject.custom')}</Tag>
      ) : null}
    </span>
  )

  // 非激活状态
  if (!isActive) {
    const displayComponent = isPortfolio
      ? portfolioComponent
      : isProject
        ? projectComponent
        : isCustom
          ? customComponent
          : null
    return (
      <div className='flex gap-2 justify-between items-center'>
        <div className='whitespace-pre-wrap'>
          {displayComponent}
          {tag}
        </div>

        {departmentIcon}
      </div>
    )
  }

  // 激活状态
  return (
    <div className='flex items-center' ref={ref}>
      {record.project ? (
        projectComponent
      ) : (
        <Tooltip
          visible={record.subprojectWorkResourceSelfId ? false : undefined}
          title={t('work.resource.subproject.inputPlaceholder')}
        >
          <WorkResourceCustomSearch
            projectWorkResourceSelfId={projectWorkResourceSelfId}
            defaultOptions={defaultOptions}
            autoClearSearchValue={false}
            value={value.subprojectWorkResourceName}
            onSearch={(v) => {
              setValue(() => ({
                displayName: v,
                subprojectWorkResourceName: v,
                subprojectWorkResourceSelfId: '',
              }))
            }}
            placeholder={t('work.resource.subproject.customPlaceholder')}
            onSelect={(v: any, option: any) => {
              setValue(() => ({
                displayName: v,
                subprojectWorkResourceName: v,
                subprojectWorkResourceSelfId: option.id,
              }))
            }}
          />
        </Tooltip>
      )}
    </div>
  )
}
